import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StickyNote, Plus, Check, X, Pencil, Trash2, Loader2, Sparkles } from 'lucide-react';

const STATI = { attiva: 'bg-emerald-100 text-emerald-700', 'da approvare': 'bg-amber-100 text-amber-700', scartata: 'bg-slate-200 text-slate-500' };

export const NoteAgenzia = ({ isAdmin }) => {
  const [note, setNote] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  const [nuovoTesto, setNuovoTesto] = useState('');
  const [nuovaCat, setNuovaCat] = useState('altro');
  const [editId, setEditId] = useState(null);
  const [editTesto, setEditTesto] = useState('');
  const [analizzando, setAnalizzando] = useState(false);

  const load = async (stato = filtroStato) => {
    try {
      const r = await api.get(`/ai-admin/note${stato ? `?stato=${encodeURIComponent(stato)}` : ''}`);
      setNote(r.data.note || []);
      setCategorie(r.data.categorie || []);
    } catch { toast.error('Errore caricamento note'); }
  };
  useEffect(() => { load(); }, [filtroStato]);

  const aggiungi = async () => {
    if (!nuovoTesto.trim()) return;
    try {
      await api.post('/ai-admin/note', { categoria: nuovaCat, testo: nuovoTesto.trim(), stato: 'attiva' });
      toast.success('Nota attiva: l\'AI la applica da subito');
      setNuovoTesto(''); load();
    } catch { toast.error('Errore salvataggio'); }
  };

  const setStato = async (n, stato) => {
    try {
      await api.put(`/ai-admin/note/${n.id}`, { stato });
      toast.success(stato === 'attiva' ? 'Nota approvata e attiva' : stato === 'scartata' ? 'Nota scartata' : 'Nota disattivata');
      load();
    } catch { toast.error('Errore'); }
  };

  const salvaModifica = async (n) => {
    try {
      await api.put(`/ai-admin/note/${n.id}`, { testo: editTesto.trim() });
      setEditId(null); load();
      toast.success('Nota aggiornata');
    } catch { toast.error('Errore'); }
  };

  const elimina = async (n) => {
    try { await api.delete(`/ai-admin/note/${n.id}`); load(); } catch { toast.error('Errore'); }
  };

  const analizzaOra = async () => {
    setAnalizzando(true);
    try {
      const r = await api.post('/ai-admin/apprendimento/esegui-ora', {}, { timeout: 120000 });
      toast.success(`Analisi completata: ${r.data.note_create || 0} nuove note da approvare`);
      setFiltroStato('da approvare');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore analisi'); }
    finally { setAnalizzando(false); }
  };

  const daApprovare = note.filter(n => n.stato === 'da approvare').length;

  return (
    <div className="space-y-4" data-testid="note-agenzia">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm flex items-center gap-2"><StickyNote size={15} /> Note dell'agenzia (knowledge base)</CardTitle>
              <p className="text-[11px] text-slate-400 mt-1">Le note ATTIVE vengono lette dall'AI a ogni conversazione, dalla più recente. Ogni sera alle 20:00 l'AI propone nuove note "da approvare" analizzando le conversazioni del giorno: nulla diventa attivo senza la tua approvazione.</p>
            </div>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={analizzaOra} disabled={analizzando} className="border-amber-300 text-amber-700" data-testid="apprendimento-ora-btn">
                {analizzando ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />}
                {analizzando ? 'Analisi...' : 'Analizza conversazioni ora'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-start">
            <select value={nuovaCat} onChange={e => setNuovaCat(e.target.value)} className="h-9 px-2 rounded-md border border-slate-200 text-xs" data-testid="nota-categoria-select">
              {categorie.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Textarea rows={1} value={nuovoTesto} onChange={e => setNuovoTesto(e.target.value)}
              placeholder="Nuova nota operativa per l'AI..." className="text-sm min-h-[36px]" data-testid="nota-testo-input" />
            <Button onClick={aggiungi} className="bg-violet-600 hover:bg-violet-700 text-white h-9" data-testid="nota-add-btn"><Plus size={14} /></Button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {['', 'attiva', 'da approvare', 'scartata'].map(s => (
              <button key={s} onClick={() => setFiltroStato(s)}
                className={`text-[11px] px-2.5 py-1 rounded-full border ${filtroStato === s ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-500'}`}
                data-testid={`filtro-${s || 'tutte'}`}>
                {s || 'Tutte'}{s === 'da approvare' && daApprovare > 0 && filtroStato !== 'da approvare' ? ` (${daApprovare})` : ''}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {note.length === 0 && <p className="text-xs italic text-slate-400">Nessuna nota.</p>}
            {note.map(n => (
              <div key={n.id} className="border rounded-lg p-2.5 bg-slate-50/60 space-y-1.5" data-testid={`nota-${n.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`border-0 text-[10px] ${STATI[n.stato] || ''}`}>{n.stato}</Badge>
                  <Badge className="border-0 text-[10px] bg-blue-100 text-blue-700">{n.categoria}</Badge>
                  <span className="text-[10px] text-slate-400">{n.data} — {n.autore}</span>
                </div>
                {editId === n.id ? (
                  <div className="space-y-1.5">
                    <Textarea rows={2} value={editTesto} onChange={e => setEditTesto(e.target.value)} className="text-xs bg-white" data-testid="nota-edit-input" />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-6 text-xs bg-emerald-600 text-white" onClick={() => salvaModifica(n)}>Salva</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditId(null)}>Annulla</Button>
                    </div>
                  </div>
                ) : <p className="text-xs text-slate-700">{n.testo}</p>}
                <div className="flex gap-2 flex-wrap">
                  {n.stato === 'da approvare' && (
                    <>
                      <button onClick={() => setStato(n, 'attiva')} className="text-[11px] flex items-center gap-1 text-emerald-600 font-semibold" data-testid={`nota-approva-${n.id}`}><Check size={11} /> Approva</button>
                      <button onClick={() => setStato(n, 'scartata')} className="text-[11px] flex items-center gap-1 text-red-500" data-testid={`nota-scarta-${n.id}`}><X size={11} /> Scarta</button>
                    </>
                  )}
                  {n.stato === 'attiva' && (
                    <button onClick={() => setStato(n, 'scartata')} className="text-[11px] flex items-center gap-1 text-slate-500" data-testid={`nota-disattiva-${n.id}`}><X size={11} /> Disattiva</button>
                  )}
                  {n.stato === 'scartata' && (
                    <button onClick={() => setStato(n, 'attiva')} className="text-[11px] flex items-center gap-1 text-emerald-600" data-testid={`nota-riattiva-${n.id}`}><Check size={11} /> Riattiva</button>
                  )}
                  <button onClick={() => { setEditId(n.id); setEditTesto(n.testo); }} className="text-[11px] flex items-center gap-1 text-amber-600" data-testid={`nota-modifica-${n.id}`}><Pencil size={11} /> Modifica</button>
                  <button onClick={() => elimina(n)} className="text-[11px] flex items-center gap-1 text-slate-400" data-testid={`nota-elimina-${n.id}`}><Trash2 size={11} /> Elimina</button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
